import { HEADING_SIZE, FONT_WEIGHT, HEADING_LINE_HEIGHT } from '@makemydeal/dr-ui-bricks-components';
import type { HeadingLevel, FontWeightName } from '@makemydeal/dr-ui-bricks-types';
import type { ResponsiveHeadingBreakpoint, ResponsiveHeadingConfig } from '../types/ResponsiveHeadingTypes';

export type { ResponsiveHeadingBreakpoint, ResponsiveHeadingConfig };

const makeBreakpoint = (fontSize: string, weight: FontWeightName = 'bold'): ResponsiveHeadingBreakpoint => ({
    fontSize,
    fontWeight: FONT_WEIGHT[weight],
    lineHeight: HEADING_LINE_HEIGHT
});

/**
 * Responsive heading configuration per breakpoint, derived entirely from
 * `HEADING_SIZE`, `FONT_WEIGHT`, and `HEADING_LINE_HEIGHT` in the ui-bricks
 * typography scale — the single source of truth for every typographic value.
 *
 * This file is intentionally separate from `constants.ts` so that the
 * ui-bricks barrel import only loads in style files, never in component files
 * that are directly imported by tests.
 *
 * Strategy: each level steps down one level on tablet, two levels on mobile.
 * Levels that would step below 8 are clamped to HEADING_SIZE[8].
 *
 * Example for level 2:
 *   desktop → fontSize: HEADING_SIZE[2] = 30px, fontWeight: 700, lineHeight: normal
 *   tablet  → fontSize: HEADING_SIZE[3] = 28px, fontWeight: 700, lineHeight: normal
 *   mobile  → fontSize: HEADING_SIZE[4] = 24px, fontWeight: 700, lineHeight: normal
 */
export const RESPONSIVE_HEADING_SIZES: Record<HeadingLevel, ResponsiveHeadingConfig> = {
    1: {
        desktop: makeBreakpoint(HEADING_SIZE[1]),
        tablet: makeBreakpoint(HEADING_SIZE[1]),
        mobile: makeBreakpoint(HEADING_SIZE[3])
    },
    2: {
        desktop: makeBreakpoint(HEADING_SIZE[2]),
        tablet: makeBreakpoint(HEADING_SIZE[2]),
        mobile: makeBreakpoint(HEADING_SIZE[4])
    },
    3: {
        desktop: makeBreakpoint(HEADING_SIZE[3]),
        tablet: makeBreakpoint(HEADING_SIZE[3]),
        mobile: makeBreakpoint(HEADING_SIZE[5])
    },
    4: {
        desktop: makeBreakpoint(HEADING_SIZE[4]),
        tablet: makeBreakpoint(HEADING_SIZE[4]),
        mobile: makeBreakpoint(HEADING_SIZE[6])
    },
    5: {
        desktop: makeBreakpoint(HEADING_SIZE[5]),
        tablet: makeBreakpoint(HEADING_SIZE[5]),
        mobile: makeBreakpoint(HEADING_SIZE[7])
    },
    6: {
        desktop: makeBreakpoint(HEADING_SIZE[6]),
        tablet: makeBreakpoint(HEADING_SIZE[6]),
        mobile: makeBreakpoint(HEADING_SIZE[8])
    },
    7: {
        desktop: makeBreakpoint(HEADING_SIZE[7]),
        tablet: makeBreakpoint(HEADING_SIZE[7]),
        mobile: makeBreakpoint(HEADING_SIZE[8])
    },
    8: {
        desktop: makeBreakpoint(HEADING_SIZE[8]),
        tablet: makeBreakpoint(HEADING_SIZE[8]),
        mobile: makeBreakpoint(HEADING_SIZE[8])
    }
};
